import { useEffect, useRef, useState } from "react";
import Header from "../../components/header";
import SideBar from "../../components/sidebar";
import Footer from "../../components/footer";
import { useNavigate, Link } from "react-router-dom";
import services from "../../services";
import Toaster from "../../components/toaster";
import constant from "../../constant";
import useIsMobile from "../../hooks/useIsMobile";
import MobileFooter from "../../components/MobileFooter";
import MobileHeader from "../../components/MobileHeader";
import { getSearchMeta, getGuestPhone, saveGuestPhone, isValidGuestPhone } from "../../utils/searchMeta";
import CitySelect from "../../components/CitySelect";
import { missingProfileFields, setReturnAfterProfile } from "../../utils/profileComplete";
import TollNote from "../../components/TollNote";
import MluxDateField from "../../components/MluxDateField";

const OneWayAvailability = () => {
    const navigate = useNavigate();
    const isMobileView = useIsMobile();
    const [toAddressList, setToAddressList] = useState([]);
    const [selectedFrom, setSelectedFrom] = useState(null);
    const [selectedTo, setSelectedTo] = useState(null);
    // From = master city, same as the home search and the partner app's one-way form
    const [citiesList, setCitiesList] = useState([]);
    const [fromCityId, setFromCityId] = useState("");
    const [toInput, setToInput] = useState("");
    const [tripDate, setTripDate] = useState("");
    // Guests give a mobile number to search; logged-in users don't see the field
    const isGuest = !localStorage.getItem('access_token');
    const [guestPhone, setGuestPhone] = useState(getGuestPhone);
    const onGuestPhone = (value) => setGuestPhone(value.replace(/\D/g, '').slice(0, 10));
    const [availableCars, setAvailableCars] = useState([]);
    const [suggestedCars, setSuggestedCars] = useState([]);
    const [loading, setLoading] = useState(false);
    const [toaster, setToaster] = useState({ visibility: "hide" });
    const [revealedVendors, setRevealedVendors] = useState({}); // Track which vendors have revealed contact info
    const autoSearch = useRef(false); // set when arriving from the home One-Way tab
    const [confirmSheet, setConfirmSheet] = useState(null); // { inquiryId, availability } while open
    const [paxCount, setPaxCount] = useState("");
    const [confirming, setConfirming] = useState(false);
    const pendingConfirm = useRef(null); // { inquiryId, availability } awaiting dialer return

    useEffect(() => {
        services.order.GET_CITIES().then((r) => setCitiesList(r.data || [])).catch(() => {});
    }, []);

    // Build a geo-feature from the city so the search/log code works unchanged
    const onCityFrom = (id) => {
        const c = (citiesList || []).find((x) => String(x.id) === String(id));
        if (!c) { setSelectedFrom(null); setFromCityId(""); return; }
        setSelectedFrom({
            properties: { full_address: c.name, coordinates: { latitude: c.latitude, longitude: c.longitude } },
            geometry: { coordinates: [c.longitude, c.latitude] },
            cityId: c.id,
        });
        setFromCityId(c.id);
    };

    useEffect(() => {
        var element = document.querySelector("body");
        element.classList.remove("home-3");
        return () => {
            element.classList.remove("home-3");
        };
    }, []);

    // Restore search state after login
    useEffect(() => {
        const onewaySearchState = localStorage.getItem('onewaySearchState');
        if (onewaySearchState) {
            try {
                const state = JSON.parse(onewaySearchState);
                // Older saved searches used a typed address; only a city pick carries cityId
                if (state.selectedFrom?.cityId) {
                    setSelectedFrom(state.selectedFrom);
                    setFromCityId(state.selectedFrom.cityId);
                }
                if (state.selectedTo) {
                    setSelectedTo(state.selectedTo);
                    setToInput(state.selectedTo.properties.full_address);
                }
                if (state.guestPhone) {
                    setGuestPhone(state.guestPhone);
                }
                if (state.tripDate) {
                    setTripDate(state.tripDate);
                }
                if (state.availableCars && state.availableCars.length > 0) {
                    setAvailableCars(state.availableCars);
                }
                if (state.suggestedCars && state.suggestedCars.length > 0) {
                    setSuggestedCars(state.suggestedCars);
                }
                // Arriving from the home One-Way tab with a route + date but no
                // results yet → run the availability search automatically.
                if (state.selectedFrom?.cityId && state.selectedTo && state.tripDate && !(state.availableCars && state.availableCars.length > 0)) {
                    autoSearch.current = true;
                }
                // Clear the stored state after restoring
                localStorage.removeItem('onewaySearchState');
            } catch (error) {
                console.error('Error restoring oneway search state:', error);
                localStorage.removeItem('onewaySearchState');
            }
        }
    }, []);

    // Auto-scroll to cars section only when exact matches are available
    useEffect(() => {
        if (availableCars.length > 0) {
            setTimeout(() => {
                const carsSection = document.getElementById('available-cars-section');
                if (carsSection) {
                    carsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 300);
        }
    }, [availableCars]);

    const scrollToCars = () => {
        const carsSection = document.getElementById('available-cars-section');
        if (carsSection) {
            carsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleContactPartner = async (availability) => {
        // If vendor details are already revealed, just call directly
        if (revealedVendors[availability.id]) {
            const contactNumber = availability.contactNumber || availability.vendorCar?.User?.phone || '';
            // Arm the confirm sheet for when the user returns from the dialer.
            pendingConfirm.current = { inquiryId: revealedVendors[availability.id], availability };
            if (contactNumber) {
                const cleanNumber = contactNumber.replace(/[^\d+]/g, '');
                window.location.href = `tel:${cleanNumber}`;
            }
            return;
        }

        const isLoggedIn = localStorage.getItem('access_token');
        
        if (!isLoggedIn) {
            // Store current search state (the login screen pre-fills the guest's number)
            saveGuestPhone(guestPhone);
            const searchState = {
                guestPhone,
                selectedFrom,
                selectedTo,
                tripDate,
                toInput,
                availableCars,
                suggestedCars
            };
            localStorage.setItem('onewaySearchState', JSON.stringify(searchState));
            // Redirect to login
            navigate('/login');
            return;
        }

        // Partners can only be contacted once the customer's profile is complete
        const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
        const missing = missingProfileFields(userDetails);
        if (missing.length) {
            localStorage.setItem('onewaySearchState', JSON.stringify({ selectedFrom, selectedTo, tripDate, toInput, availableCars, suggestedCars }));
            setReturnAfterProfile('/oneway');
            navigate('/myprofile', { state: { reason: `Add your ${missing.join(', ')} to contact partners.` } });
            return;
        }

        // User is logged in, create inquiry and show vendor details
        try {
            const payload = {
                vendorOneWayAvailabilityId: availability.id,
                userName: `${userDetails.firstName || ''} ${userDetails.lastName || ''}`.trim() || 'User',
                contactNumber: userDetails.phoneNumber || (userDetails.countryCode || '+91') + userDetails.phoneNumber || ''
            };

            setLoading(true);
            const createResp = await services.order.CREATE_ONE_WAY_INQUIRY(payload);
            const inquiryId = createResp?.data?.id;

            // Mark this vendor as revealed (store the inquiry id for the confirm step)
            setRevealedVendors(prev => ({
                ...prev,
                [availability.id]: inquiryId || true
            }));
            // Arm the confirm sheet for when the user returns from the dialer.
            pendingConfirm.current = { inquiryId, availability };

            // Show vendor contact and redirect to dialer
            const contactNumber = availability.contactNumber || availability.vendorCar?.User?.phone || '';
            if (contactNumber) {
                // Remove any non-digit characters except +
                const cleanNumber = contactNumber.replace(/[^\d+]/g, '');
                window.location.href = `tel:${cleanNumber}`;
            } else {
                setToaster({
                    visibility: "show",
                    type: "error",
                    message: "Contact number not available",
                });
                setTimeout(() => {
                    setToaster({ visibility: "hide" });
                }, 3000);
            }
        } catch (err) {
            console.log(err);
            setToaster({
                visibility: "show",
                type: "error",
                message: err.response?.data?.message || "Failed to create inquiry",
            });
            setTimeout(() => {
                setToaster({ visibility: "hide" });
            }, 3000);
        } finally {
            setLoading(false);
        }
    };

    const GET_LOCATION_DETAILS = async (searchKey) => {
        if (searchKey.length < 3) {
            setToAddressList([]);
            return;
        }

        try {
            const response = await services.order.GET_LOCATION(searchKey);
            setToAddressList(response.data.features || []);
        } catch (err) {
            console.log(err);
            setToAddressList([]);
        }
    };

    const handleToInputChange = (e) => {
        const value = e.target.value;
        setToInput(value);
        if (selectedTo && value !== selectedTo.properties.full_address) {
            setSelectedTo(null);
        }
        GET_LOCATION_DETAILS(value);
    };

    const handleToSelect = (item) => {
        setSelectedTo(item);
        setToInput(item.properties.full_address);
        setToAddressList([]);
    };

    const handleSearch = async () => {
        if (!selectedFrom || !selectedTo || !tripDate) {
            setToaster({
                visibility: "show",
                type: "error",
                message: "Please fill all fields (From city, To, and Trip Date)",
            });
            setTimeout(() => {
                setToaster({ visibility: "hide" });
            }, 3000);
            return;
        }
        if (isGuest && !isValidGuestPhone(guestPhone)) {
            setToaster({ visibility: "show", type: "error", message: "Enter your 10-digit mobile number to search" });
            setTimeout(() => setToaster({ visibility: "hide" }), 3000);
            return;
        }
        if (isGuest) saveGuestPhone(guestPhone);

        setLoading(true);
        try {
            const fromCoordinates = JSON.stringify(selectedFrom.properties.coordinates);
            const toCoordinates = JSON.stringify(selectedTo.properties.coordinates);
            const tripDateISO = new Date(tripDate).toISOString();

            const response = await services.order.ONE_WAY_AVAILABILITIES(
                tripDateISO,
                fromCoordinates,
                toCoordinates,
                { ...getSearchMeta(selectedFrom, selectedTo), ...(isGuest ? { guestPhone } : {}) }
            );

            const data = response.data || {};
            const available = data.availableCars || [];
            const suggested = data.suggestedCars || [];

            setAvailableCars(available);
            setSuggestedCars(suggested);

            if (available.length === 0 && suggested.length === 0) {
                setToaster({
                    visibility: "show",
                    type: "info",
                    message: "No cars available for this route and date",
                });
                setTimeout(() => {
                    setToaster({ visibility: "hide" });
                }, 3000);
            }
        } catch (err) {
            console.log(err);
            setToaster({
                visibility: "show",
                type: "error",
                message: err.response?.data?.message || "Failed to fetch availability",
            });
            setTimeout(() => {
                setToaster({ visibility: "hide" });
            }, 3000);
            setAvailableCars([]);
            setSuggestedCars([]);
        } finally {
            setLoading(false);
        }
    };

    // Fire the search once the handed-off route + date have populated state.
    useEffect(() => {
        if (autoSearch.current && selectedFrom && selectedTo && tripDate) {
            autoSearch.current = false;
            handleSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFrom, selectedTo, tripDate]);

    // When the app regains focus after the dialer, pop the "trip confirmed?" sheet.
    useEffect(() => {
        const onReturn = () => {
            if (pendingConfirm.current) {
                setConfirmSheet(pendingConfirm.current);
                setPaxCount("");
                pendingConfirm.current = null;
            }
        };
        const onVis = () => { if (document.visibilityState === 'visible') onReturn(); };
        window.addEventListener('focus', onReturn);
        document.addEventListener('visibilitychange', onVis);
        return () => {
            window.removeEventListener('focus', onReturn);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, []);

    const confirmTrip = async () => {
        const pax = Number(paxCount);
        if (!pax || pax < 1) {
            setToaster({ visibility: "show", type: "error", message: "Enter number of passengers" });
            setTimeout(() => setToaster({ visibility: "hide" }), 3000);
            return;
        }
        try {
            setConfirming(true);
            await services.order.CONFIRM_ONE_WAY_INQUIRY(confirmSheet.inquiryId, { passengerCount: pax });
            setConfirmSheet(null);
            setToaster({ visibility: "show", type: "success", message: "Trip confirmed! See it in My Bookings → One-way." });
            setTimeout(() => setToaster({ visibility: "hide" }), 3500);
        } catch (err) {
            setToaster({ visibility: "show", type: "error", message: err.response?.data?.message || "Could not confirm trip" });
            setTimeout(() => setToaster({ visibility: "hide" }), 3000);
        } finally {
            setConfirming(false);
        }
    };

    if (isMobileView) {
        const isLoggedIn = localStorage.getItem('access_token');
        const suggestedDates = [...new Set(suggestedCars.map(c => new Date(c.tripDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })))];
        return (
            <>
                <Toaster type={toaster.type} title={toaster.title} text={toaster.message || toaster.text} visiblity={toaster.visibility || toaster.visiblity} />
                <SideBar />
                <main className="mlux">
                    <MobileHeader back="/" />

                    <div className="mlux-hero-gradient" data-rise="1">
                        <span className="chip">Up to 40% off</span>
                        <h2>One-way, <em>half the fare</em></h2>
                        <p>Grab a cab that's already heading your way — pay only for your side.</p>
                        <i className="fas fa-route deco"></i>
                    </div>

                    <div className="mlux-card mlux-search" style={{ marginTop: 14 }} data-rise="2">
                        <CitySelect variant="mobile" icon="far fa-location-dot" placeholder="From city" cities={citiesList} value={fromCityId} onChange={onCityFrom} />

                        <div className="mlux-field" key={'t' + (selectedTo?.properties?.full_address || '')}>
                            <i className="fas fa-flag-checkered"></i>
                            <input type="text" placeholder="To" value={toInput} onChange={handleToInputChange} />
                        </div>
                        {toAddressList?.length > 0 && <ul className="mlux-suggest">
                            {toAddressList.map((item, i) => <li key={i} onClick={() => handleToSelect(item)}><i className="far fa-location-dot"></i>{item?.properties?.full_address}</li>)}
                        </ul>}

                        <MluxDateField value={tripDate} min={new Date().toLocaleDateString('en-CA')} placeholder="Travel date" onChange={setTripDate} />

                        {isGuest && (
                            <div className="mlux-field"><i className="far fa-mobile"></i>
                                <input type="tel" inputMode="numeric" value={guestPhone} placeholder="Your mobile number" onChange={(e) => onGuestPhone(e.target.value)} />
                            </div>
                        )}

                        <button className="mlux-btn" disabled={loading} onClick={handleSearch}>
                            <i className="fas fa-magnifying-glass"></i> {loading ? 'Searching…' : 'Search availability'}
                        </button>
                    </div>

                    {availableCars.length === 0 && suggestedDates.length > 0 && (
                        <div className="mlux-card" style={{ padding: 14, marginTop: 14, background: 'rgba(56,132,255,0.08)', border: '1px solid rgba(56,132,255,0.2)' }} data-rise="2">
                            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>Not available on that date</div>
                            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>Try these dates instead:</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                {suggestedDates.map((d, i) => <span key={i} className="mlux-chip" style={{ boxShadow: 'none' }}>{d}</span>)}
                            </div>
                        </div>
                    )}

                    {availableCars.length === 0 && suggestedDates.length === 0 && (
                        <>
                            <div className="mlux-sec-head"><h3>How it works</h3></div>
                            <div className="mlux-steps" data-rise="3">
                                <div className="mlux-step"><span className="n">1</span><div><h4>Enter your route</h4><p>Pick your from, to &amp; travel date</p></div></div>
                                <div className="mlux-step"><span className="n">2</span><div><h4>See empty-return cabs</h4><p>Vendors already heading your way</p></div></div>
                                <div className="mlux-step"><span className="n">3</span><div><h4>Contact the partner</h4><p>Call &amp; book the driver directly</p></div></div>
                            </div>
                        </>
                    )}

                    {availableCars.length > 0 && <div className="mlux-sec-head"><h3>Available cars</h3><span style={{ color: 'var(--muted)', fontSize: 13, fontWeight: 700 }}>{availableCars.length}</span></div>}
                    <div id="available-cars-section">
                        {availableCars.map((av, i) => {
                            const carPhoto = av.vendorCar?.photos?.[0] || av.vendorCar?.Car?.photos?.[0] || av.vendorCar?.car?.photos?.[0];
                            const carName = av.vendorCar?.Car?.name || av.vendorCar?.car?.name || "Car";
                            const carBrand = av.vendorCar?.Car?.brand || av.vendorCar?.car?.brand || "";
                            const seats = av.vendorCar?.Car?.customerCapacity || av.vendorCar?.car?.seatingCapacity || av.vendorCar?.Car?.seatingCapacity;
                            const perKm = Number(av.vendorCar?.perKmCharge || av.vendorCar?.Car?.perKmCharge || 0);
                            const estFare = av.distanceMeters && perKm ? Math.round((av.distanceMeters / 1000) * perKm) : null;
                            const revealed = revealedVendors[av.id];
                            return (
                                <div className="mlux-card mlux-car" key={av.id || i} data-rise="3">
                                    <div className="ph">
                                        {carPhoto
                                            ? <img src={constant.IMAGE_URL + carPhoto} crossOrigin="anonymous" alt={`${carBrand} ${carName}`} />
                                            : <div style={{ height: '100%', display: 'grid', placeItems: 'center', color: '#94a3b8', fontSize: 42 }}><i className="far fa-car"></i></div>}
                                    </div>
                                    <div className="bd">
                                        <div className="nm">{carBrand} {carName}</div>
                                        <div className="perkm">RC: {av.vendorCar?.rcNumber || 'N/A'}</div>
                                        <div className="mlux-route" style={{ marginTop: 12 }}>
                                            <div className="pt from"><small>From</small>{av.from?.full_address || 'N/A'}</div>
                                            <div className="pt to"><small>To</small>{av.to?.full_address || 'N/A'}</div>
                                        </div>
                                        <div className="mlux-date"><i className="far fa-calendar"></i>{new Date(av.tripDate).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}{seats ? ` · ${seats} seats` : ''}</div>
                                        {estFare != null && <><div className="mlux-date" style={{ color: '#16a34a', fontWeight: 800 }}><i className="fas fa-indian-rupee-sign"></i> Est. fare ₹{estFare}</div><TollNote style={{ marginTop: -4, marginBottom: 6 }} /></>}
                                        {revealed && <div className="feats"><span className="feat"><i className="far fa-user"></i> {av.vendorCar?.User?.firstName || ''} {av.vendorCar?.User?.lastName || ''}</span><span className="feat"><i className="far fa-phone"></i> {av.contactNumber || 'N/A'}</span></div>}
                                        <button className="mlux-btn" disabled={loading} onClick={() => handleContactPartner(av)}>
                                            <i className="far fa-phone"></i> {revealed ? 'Call partner' : 'Get contact'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </main>
                {isLoggedIn && <MobileFooter />}
                {confirmSheet && (
                    <div className="mlux-sheet-backdrop" onClick={() => setConfirmSheet(null)}>
                        <div className="mlux-sheet" onClick={(e) => e.stopPropagation()}>
                            <div className="mlux-sheet-grip" />
                            <h3>Did you confirm this trip?</h3>
                            <p>Confirm with the partner on call, then let us know so we can save it to your bookings.</p>
                            <label className="mlux-lbl">How many passengers?</label>
                            <div className="mlux-field no-icon">
                                <input type="number" min="1" inputMode="numeric" value={paxCount} placeholder="No. of passengers" onChange={(e) => setPaxCount(e.target.value)} />
                            </div>
                            <button className="mlux-btn" disabled={confirming} onClick={confirmTrip}>
                                <i className="far fa-circle-check"></i> {confirming ? 'Saving…' : 'Yes, confirm booking'}
                            </button>
                            <button className="mlux-btn ghost" style={{ marginTop: 10 }} onClick={() => setConfirmSheet(null)}>Not yet</button>
                        </div>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <Header />
            <SideBar />
            <main className="main">
                <div className="booking-area" style={{ paddingTop: "100px", paddingBottom: "50px" }}>
                    <div className="container">
                        <div className="booking-form">
                            <h4 className="booking-title">Find One-Way Car Availability</h4>

                            <div className="row">
                                <div className="col-lg-4">
                                    <div className="form-group">
                                        <label>From city</label>
                                        <CitySelect placeholder="Select city" cities={citiesList} value={fromCityId} onChange={onCityFrom} />
                                    </div>
                                </div>

                                <div className="col-lg-4">
                                    <div className="form-group" key={selectedTo?.properties?.full_address}>
                                        <label>To</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Type location"
                                            value={toInput}
                                            onChange={handleToInputChange}
                                        />
                                        <i className="far fa-location-dot"></i>
                                        {toAddressList?.length > 0 && (
                                            <ul className="suggesting-dropdown">
                                                {toAddressList.map((item, index) => (
                                                    <div key={index}>
                                                        <li
                                                            onClick={() => handleToSelect(item)}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            {item?.properties?.full_address}
                                                        </li>
                                                        {index < toAddressList.length - 1 && <hr />}
                                                    </div>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                                <div className="col-lg-4">
                                    <div className="form-group">
                                        <label>Trip Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={tripDate}
                                            onChange={(e) => setTripDate(e.target.value)}
                                            min={new Date().toISOString().split("T")[0]}
                                        />
                                        <i className="far fa-calendar"></i>
                                    </div>
                                </div>

                                {isGuest && (
                                    <div className="col-lg-4">
                                        <div className="form-group">
                                            <label>Mobile Number</label>
                                            <input
                                                type="tel"
                                                inputMode="numeric"
                                                className="form-control"
                                                placeholder="10-digit mobile"
                                                value={guestPhone}
                                                onChange={(e) => onGuestPhone(e.target.value)}
                                            />
                                            <i className="far fa-mobile"></i>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {availableCars.length === 0 && suggestedCars.length > 0 && (
                                <div className="row">
                                    <div className="col-lg-12">
                                        <div className="alert alert-info" style={{ 
                                            marginTop: "20px", 
                                            marginBottom: "0",
                                            padding: "15px", 
                                            backgroundColor: "#e7f3ff", 
                                            border: "1px solid #b3d9ff",
                                            borderRadius: "5px"
                                        }}>
                                            <p style={{ margin: "0 0 10px 0", fontSize: "16px" }}>
                                                Cars are not available for your given date. Following dates are available:
                                            </p>
                                            <div style={{ marginTop: "10px", marginBottom: "15px" }}>
                                                {[...new Set(suggestedCars.map(car => {
                                                    const date = new Date(car.tripDate);
                                                    return date.toLocaleDateString('en-US', { 
                                                        year: 'numeric', 
                                                        month: 'long', 
                                                        day: 'numeric' 
                                                    });
                                                }))].map((dateStr, index) => (
                                                    <span key={index} style={{ 
                                                        fontWeight: "bold", 
                                                        fontSize: "16px",
                                                        color: "#0066cc",
                                                        marginRight: "15px"
                                                    }}>
                                                        {dateStr}
                                                    </span>
                                                ))}
                                            </div>
                                            <button
                                                onClick={scrollToCars}
                                                className="theme-btn btn-sm"
                                                style={{
                                                    width: "100%",
                                                    marginTop: "10px"
                                                }}
                                            >
                                                View Available Cars <i className="fas fa-arrow-down"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="row">
                                <div className="col-lg-12 text-center">
                                    <button
                                        className="theme-btn"
                                        onClick={handleSearch}
                                        disabled={loading}
                                        style={{ marginTop: "20px" }}
                                    >
                                        {loading ? "Searching..." : "Search Availability"}
                                        <i className="fas fa-arrow-right"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {(availableCars.length > 0 || suggestedCars.length > 0) && (
                    <div id="available-cars-section" className="taxi-area">
                        <div className="container">
                            {availableCars.length > 0 && (
                                <>
                                    <div className="row">
                                        <div className="col-lg-6 mx-auto">
                                            <div className="site-heading text-center mb-0">
                                                <span className="site-title-tagline">Available Cars</span>
                                                <h3>One-Way Car Availability</h3>
                                                <div className="heading-divider"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        {availableCars.map((availability, index) => {
                                            const carPhoto = availability.vendorCar?.photos?.[0] || availability.vendorCar?.Car?.photos?.[0] || availability.vendorCar?.car?.photos?.[0];
                                            const carName = availability.vendorCar?.Car?.name || availability.vendorCar?.car?.name || "Car";
                                            const carBrand = availability.vendorCar?.Car?.brand || availability.vendorCar?.car?.brand || "N/A";
                                            const rcNumber = availability.vendorCar?.rcNumber || "N/A";
                                    
                                    return (
                                        <div key={availability.id || index} className="col-md-6 col-lg-4 mb-4">
                                            <div className="taxi-item">
                                                <div className="taxi-img">
                                                    {carPhoto ? (
                                                        <img 
                                                            src={constant.IMAGE_URL + carPhoto} 
                                                            crossOrigin="anonymous" 
                                                            style={{ height: '150px', width: '100%', objectFit: 'contain' }}
                                                            alt={`${carBrand} ${carName}`}
                                                        />
                                                    ) : (
                                                        <div style={{ height: '150px', width: '100%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <i className="far fa-car" style={{ fontSize: '48px', color: '#999' }}></i>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="taxi-content">
                                                    <div className="taxi-head">
                                                        <h4>{carBrand} {carName}</h4>
                                                        <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                                                            <strong>RC Number:</strong> {rcNumber}
                                                        </p>
                                                        {/* <span>Rs {availability.vendorCar?.perKmCharge || "N/A"}/km</span> */}
                                                    </div>
                                                    <div className="taxi-feature">
                                                        <ul>
                                                            <li>
                                                                <i className="far fa-map-location-dot"></i> From: <span>{availability.from?.full_address || "N/A"}</span>
                                                            </li>
                                                            <li>
                                                                <i className="far fa-map-location-dot"></i> To: <span>{availability.to?.full_address || "N/A"}</span>
                                                            </li>
                                                            <li>
                                                                <i className="far fa-calendar"></i> Trip Date: <span>{new Date(availability.tripDate).toLocaleDateString('en-IN')}</span>
                                                            </li>
                                                            <li>
                                                                <i className="far fa-person-seat"></i> Passengers: <span>{availability.vendorCar?.Car?.customerCapacity || availability.vendorCar?.car?.seatingCapacity || availability.vendorCar?.Car?.seatingCapacity || "N/A"}</span>
                                                            </li>
                                                            {/* <li>
                                                                <i className="far fa-suitcase-rolling"></i> Luggage Carry: <span><b>{availability.vendorCar?.isCarrierAvailable ? 'Yes' : 'No'}</b></span>
                                                            </li>
                                                            <li>
                                                                <i className="far fa-heat"></i> Air Condition: <span><b>{availability.vendorCar?.isAcAvailable ? 'Yes' : 'No'}</b></span>
                                                            </li> */}
                                                            {revealedVendors[availability.id] && (
                                                                <>
                                                                    <li>
                                                                        <i className="far fa-user"></i> Vendor: <span>{availability.vendorCar?.User?.firstName || availability.vendorCar?.User?.name || ""} {availability.vendorCar?.User?.lastName || ""}</span>
                                                                    </li>
                                                                    <li>
                                                                        <i className="far fa-phone"></i> Contact: <span>{availability.contactNumber || "N/A"}</span>
                                                                    </li>
                                                                    {availability.alternativeContactNumber && (
                                                                        <li>
                                                                            <i className="far fa-phone"></i> Alt. Contact: <span>{availability.alternativeContactNumber}</span>
                                                                        </li>
                                                                    )}
                                                                </>
                                                            )}
                                                        </ul>
                                                    </div>
                                                    <button
                                                        onClick={() => handleContactPartner(availability)}
                                                        className="theme-btn"
                                                        style={{ width: "100%", marginTop: "15px" }}
                                                        disabled={loading}
                                                    >
                                                        {revealedVendors[availability.id] ? (
                                                            <>
                                                                <i className="far fa-phone"></i> Call Partner
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="far fa-phone"></i> Contact Partner
                                                            </>
                                                        )}
                                                    </button>
                                                    {/* <div className="mt-3">
                                                        <span
                                                            className={`badge ${availability.status && availability.isActive
                                                                ? "bg-success"
                                                                : "bg-secondary"
                                                            }`}
                                                            style={{ padding: '8px 12px', fontSize: '14px' }}
                                                        >
                                                            {availability.status && availability.isActive
                                                                ? "Available"
                                                                : "Unavailable"}
                                                        </span>
                                                    </div> */}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                    </div>
                                </>
                            )}

                            {availableCars.length === 0 && suggestedCars.length > 0 && (
                                <>
                                    <div className="row">
                                        <div className="col-lg-6 mx-auto">
                                            <div className="site-heading text-center mb-0">
                                                <span className="site-title-tagline">Suggested Cars</span>
                                                <h3>Alternative Options Available</h3>
                                                <p className="text-muted">No exact matches found for your selected date. Here are some suggested cars for the same route:</p>
                                                <div className="heading-divider"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        {suggestedCars.map((availability, index) => {
                                            const carPhoto = availability.vendorCar?.photos?.[0] || availability.vendorCar?.Car?.photos?.[0] || availability.vendorCar?.car?.photos?.[0];
                                            const carName = availability.vendorCar?.Car?.name || availability.vendorCar?.car?.name || "Car";
                                            const carBrand = availability.vendorCar?.Car?.brand || availability.vendorCar?.car?.brand || "N/A";
                                            const rcNumber = availability.vendorCar?.rcNumber || "N/A";
                                            
                                            return (
                                                <div key={availability.id || index} className="col-md-6 col-lg-4 mb-4">
                                                    <div className="taxi-item">
                                                        <div className="taxi-img">
                                                            {carPhoto ? (
                                                                <img 
                                                                    src={constant.IMAGE_URL + carPhoto} 
                                                                    crossOrigin="anonymous" 
                                                                    style={{ height: '150px', width: '100%', objectFit: 'contain' }}
                                                                    alt={`${carBrand} ${carName}`}
                                                                />
                                                            ) : (
                                                                <div style={{ height: '150px', width: '100%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <i className="far fa-car" style={{ fontSize: '48px', color: '#999' }}></i>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="taxi-content">
                                                            <div className="taxi-head">
                                                                <h4>{carBrand} {carName}</h4>
                                                                <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                                                                    <strong>RC Number:</strong> {rcNumber}
                                                                </p>
                                                                {/* <span>Rs {availability.vendorCar?.perKmCharge || "N/A"}/km</span> */}
                                                            </div>
                                                            <div className="taxi-feature">
                                                                <ul>
                                                                    <li>
                                                                        <i className="far fa-map-location-dot"></i> From: <span>{availability.from?.full_address || availability.from?.city || "N/A"}</span>
                                                                    </li>
                                                                    <li>
                                                                        <i className="far fa-map-location-dot"></i> To: <span>{availability.to?.full_address || availability.to?.city || "N/A"}</span>
                                                                    </li>
                                                                    <li>
                                                                        <i className="far fa-calendar"></i> Trip Date: <span>{new Date(availability.tripDate).toLocaleDateString('en-IN')}</span>
                                                                    </li>
                                                                    <li>
                                                                        <i className="far fa-person-seat"></i> Passengers: <span>{availability.vendorCar?.Car?.customerCapacity || availability.vendorCar?.car?.seatingCapacity || availability.vendorCar?.Car?.seatingCapacity || "N/A"}</span>
                                                                    </li>
                                                                    {/* <li>
                                                                        <i className="far fa-suitcase-rolling"></i> Luggage Carry: <span><b>{availability.vendorCar?.isCarrierAvailable ? 'Yes' : 'No'}</b></span>
                                                                    </li>
                                                                    <li>
                                                                        <i className="far fa-heat"></i> Air Condition: <span><b>{availability.vendorCar?.isAcAvailable ? 'Yes' : 'No'}</b></span>
                                                                    </li> */}
                                                                    {revealedVendors[availability.id] && (
                                                                        <>
                                                                            <li>
                                                                                <i className="far fa-user"></i> Vendor: <span>{availability.vendorCar?.User?.firstName || availability.vendorCar?.User?.name || ""} {availability.vendorCar?.User?.lastName || ""}</span>
                                                                            </li>
                                                                            <li>
                                                                                <i className="far fa-phone"></i> Contact: <span>{availability.contactNumber || "N/A"}</span>
                                                                            </li>
                                                                            {availability.alternativeContactNumber && (
                                                                                <li>
                                                                                    <i className="far fa-phone"></i> Alt. Contact: <span>{availability.alternativeContactNumber}</span>
                                                                                </li>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </ul>
                                                            </div>
                                                            <button
                                                                onClick={() => handleContactPartner(availability)}
                                                                className="theme-btn"
                                                                style={{ width: "100%", marginTop: "15px" }}
                                                                disabled={loading}
                                                            >
                                                                {revealedVendors[availability.id] ? (
                                                                    <>
                                                                        <i className="far fa-phone"></i> Call Partner
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="far fa-phone"></i> Contact Partner
                                                                    </>
                                                                )}
                                                            </button>
                                                            {/* <div className="mt-3">
                                                                <span
                                                                    className={`badge ${availability.status && availability.isActive
                                                                        ? "bg-success"
                                                                        : "bg-secondary"
                                                                    }`}
                                                                    style={{ padding: '8px 12px', fontSize: '14px' }}
                                                                >
                                                                    {availability.status && availability.isActive
                                                                        ? "Available"
                                                                        : "Unavailable"}
                                                                </span>
                                                            </div> */}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {availableCars.length === 0 && suggestedCars.length === 0 && !loading && (
                    <div className="container text-center" style={{ paddingBottom: "50px" }}>
                        <p>No results to display. Please search for availability.</p>
                    </div>
                )}
            </main>
            <Footer />
            <Toaster toaster={toaster} />
        </>
    );
};

export default OneWayAvailability;

